// React
import React, { useEffect, useState } from 'react';

// Mantine
import { Select, TextInput } from '@mantine/core';
import { MRT_ColumnDef } from 'mantine-react-table';

// Interface
import { Organization, Role, User } from 'interfaces';

// Custom
import { DataBadge } from 'components/common/DataBadge';
import { StatusBadge } from 'components/common/StatusBadge';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { useCurrentSiteStore } from 'store/SiteStore';

export const UserColumns = (
  organizationProp: Organization[],
  roleProp: Role[]
) => {
  const isAdmin = useIsAdmin();
  const columns: MRT_ColumnDef<User>[] = [
    {
      accessorKey: 'firstName',
      header: 'First Name',
      Edit: ({ cell }) => {
        const [selectedValue, setSelectedValue] = useState(
          cell.getValue() as string
        );
        const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;
          setSelectedValue(newValue);
          cell.row._valuesCache.firstName = newValue;
        };

        return (
          <TextInput
            label="First Name"
            value={selectedValue}
            onChange={handleValueChange}
            placeholder="First Name"
            data-testid="firstNameTextInput"
            required
            errorProps={{ 'data-testid': 'firstNameTextInputError' }}
          />
        );
      },
      accessorFn: (row) => row.firstName,
    },
    {
      accessorKey: 'lastName',
      header: 'Last Name',
      accessorFn: (row) => row.lastName,
      Edit: ({ cell }) => {
        const [selectedValue, setSelectedValue] = useState(
          cell.getValue() as string
        );
        const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;
          setSelectedValue(newValue);
          cell.row._valuesCache.lastName = newValue;
        };

        return (
          <TextInput
            label="Last Name"
            value={selectedValue}
            onChange={handleValueChange}
            placeholder="Last Name"
            data-testid="lastNameTextInput"
            required
            errorProps={{ 'data-testid': 'lastNameTextInputError' }}
          />
        );
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
      Edit: ({ cell }) => {
        const [selectedValue, setSelectedValue] = useState(
          cell.getValue() as string
        );
        const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;
          setSelectedValue(newValue);
          cell.row._valuesCache.email = newValue; // Update the cell value cache
        };

        return (
          <TextInput
            label="Email"
            value={selectedValue}
            onChange={handleValueChange}
            placeholder="Email Address"
            data-testid="emailTextInput"
            required
          />
        );
      },
    },
    {
      accessorKey: 'phone',
      header: 'Phone Number',
      accessorFn: (row) => row.phone,
      Edit: ({ cell }) => {
        const [selectedValue, setSelectedValue] = useState(
          cell.getValue() as string
        );
        const [inputError, setInputError] = useState<boolean>(false);
        const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;
          const match = newValue.match(/^\+1\d{10}$/);
          if (!match) {
            setInputError(true);
          } else {
            setInputError(false);
          }
          setSelectedValue(newValue);
          cell.row._valuesCache.phone = newValue;
        };

        return (
          <TextInput
            label="Phone Number"
            value={selectedValue}
            onChange={handleValueChange}
            placeholder="Phone Number"
            data-testid="phoneTextInput"
            required
            error={
              inputError
                ? "Phone number must be in the format '+1##########'"
                : null
            }
          />
        );
      },
    },
    {
      accessorKey: 'roleID',
      header: 'Role',
      Edit: ({ cell }) => {
        const roles = roleProp.filter((role) => role.name !== 'Provider');
        const selectedValue = cell.getValue() as string;
        const [selectedRoleID, setSelectedRoleID] =
          useState<string>(selectedValue);
        const { organizationID } = useCurrentSiteStore();

        const handleRoleChange = (value: string) => {
          cell.row._valuesCache.roleID = value;
          setSelectedRoleID(value);
          const role = roles.find((role) => role.id === value);
          cell.row._valuesCache.role = role;
        };

        useEffect(() => {
          cell.row._valuesCache.organizationID = organizationID;
          cell.row._valuesCache.organization = organizationProp.find(
            (org) => org.id === organizationID
          );
        }, [organizationID]);

        return (
          <Select
            label="Role"
            data-testid="roleSelect"
            data={roles
              .filter(
                (role) =>
                  role.organizationID === organizationID ||
                  role.organizationID === null
              )
              .map((role) => ({
                value: role.id,
                label: role.name,
              }))}
            value={selectedRoleID}
            key={cell.id}
            disabled={!isAdmin && !organizationID ? true : false}
            onChange={handleRoleChange}
            allowDeselect
          />
        );
      },

      Cell: ({ cell }) => {
        const roles = roleProp;
        const selectedRoleID = cell.getValue() as string;
        const roleObject = roles.find((role) => role.id === selectedRoleID);

        return <DataBadge>{roleObject?.name}</DataBadge>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Account Status',
      Edit: () => <></>,
      Cell: ({ cell }) => (
        <StatusBadge active={cell.getValue() === 'active'}>
          {cell.getValue() as string}
        </StatusBadge>
      ),
    },
  ];
  return columns;
};
